import { ExclamationCircleFilled, SyncOutlined } from "@ant-design/icons";
import { type ModalFuncProps } from "antd/es/modal";
import useModal, { ModalFuncWithPromise } from "antd/es/modal/useModal";
import { NotificationInstance } from "antd/es/notification/interface";
import useNotification from "antd/es/notification/useNotification";
import classNames from "classnames";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import QueryProvider from "./query";
import {
  getValueByPath,
  GlobalQueryClient,
  setValueByPath,
} from "@/utils/shared";
import { QueryClientProvider } from "@tanstack/react-query";

export interface GlobalContextProps {
  hintPrompt: (
    type: Exclude<keyof NotificationInstance, "destroy">,
    title: string,
    content?: string,
  ) => void;
  menuCollapse: boolean;
  changeMenuCollapse: (flag: boolean) => void;
  copyText: (value: string) => void;
  modalPrompt: (
    type: Exclude<NonNullable<ModalFuncProps["type"]>, "warn">,
    props: ModalFuncProps,
  ) => ReturnType<ModalFuncWithPromise>;
  openConfirm: (
    content: string | ReactNode,
    onExecute: () => Promise<any>,
    onSuccess?: () => void,
    onFail?: (error: Error) => void,
    onCancel?: () => void,
    className?: string,
    icon?: ReactNode,
  ) => Promise<boolean>;
  getLocalStorageData: <T = unknown>(
    key: string,
    defaultValue?: T,
  ) => T | undefined;
  setLocalStorageData: (
    key: string,
    value: string | number | boolean | undefined | null,
  ) => void;
}

const GlobalContext = createContext<GlobalContextProps | null>(null);
GlobalContext.displayName = "GlobalContext";

const DefaultModalIcon = <ExclamationCircleFilled />;

const BotAutoPortalLocalStorageKey = "bot-auto-portal";
const BotAutoPortalLocalStorageDefault = {};
function handlerBotAutoLocalStorage(
  event: StorageEvent,
  cb: (value: any) => void,
) {
  if (event.key === BotAutoPortalLocalStorageKey) {
    try {
      cb(event.newValue ? JSON.parse(event.newValue) : {});
    } catch (error) {
      console.error(
        "Error syncing localStorage key:",
        BotAutoPortalLocalStorageKey,
        error,
      );
    }
  }
}

const GlobalContextProvider = ({ children }: { children?: ReactNode }) => {
  const [notificationAPI, notificationContext] = useNotification();
  const [modalApi, contextHolder] = useModal();
  const [localStorageValue, setLocalStorageValue] = useState(() => {
    try {
      const item = localStorage.getItem(BotAutoPortalLocalStorageKey);
      return item ? JSON.parse(item) : BotAutoPortalLocalStorageDefault;
    } catch (error) {
      console.error(
        "Error reading localStorage key:",
        BotAutoPortalLocalStorageKey,
        error,
      );
      return { ...BotAutoPortalLocalStorageDefault };
    }
  });

  const modalPrompt = useCallback(
    (
      type: Exclude<NonNullable<ModalFuncProps["type"]>, "warn">,
      props: ModalFuncProps,
    ) => {
      return modalApi[type](props);
    },
    [modalApi],
  );

  const [menuCollapse, changeMenuCollapse] = useState<boolean>(false);

  const hintPrompt = useCallback(
    (
      type: Exclude<keyof NotificationInstance, "destroy">,
      title: string,
      content: string = "",
    ) => {
      notificationAPI[type]({
        placement: "topRight",
        message: <div>{title}</div>,
        description: <div>{content}</div>,
        className: "global__notification-wrapper",
        duration: 3,
      });
    },
    [notificationAPI],
  );

  const copyText = useCallback(
    (value: string) => {
      try {
        // navigator.clipboard.writeText(value);
        hintPrompt("success", "Copied", value);
      } catch (err) {
        hintPrompt("error", "Copy failede", `error: ${err ?? "unknown error"}`);
        console.error("Unable to copy to clipboard", err);
      }
    },
    [hintPrompt],
  );

  const openConfirm = useCallback(
    (
      content: string | ReactNode,
      onExecute: () => Promise<boolean>,
      onSuccess?: () => void,
      onFail?: (error: Error) => void,
      onCancel?: () => void,
      className?: string,
      icon: ReactNode = DefaultModalIcon,
    ): Promise<boolean> => {
      const promise = new Promise<boolean>((resolve, reject) => {
        const modalObj = modalPrompt("confirm", {
          className: classNames(className),
          title: "Confirm",
          icon: icon,
          content,
          keyboard: false,
          okText: "Confirm",
          cancelText: "Cancel",
          okButtonProps: { disabled: false },
          cancelButtonProps: { disabled: false },
          onCancel: onCancel,
          async onOk() {
            modalObj.update({
              icon: <SyncOutlined spin />,
              okButtonProps: { disabled: true },
              cancelButtonProps: { disabled: true },
            });
            onExecute()
              .then((res) => {
                if (res) {
                  onSuccess?.();
                  resolve(true);
                } else {
                  modalObj.update({
                    icon: <ExclamationCircleFilled className="text-red-500" />,
                    okButtonProps: { disabled: false },
                    cancelButtonProps: { disabled: false },
                  });
                  onFail?.(new Error("failed"));
                  reject("failed");
                }
              })
              .catch((error) => {
                modalObj.update({
                  icon: <ExclamationCircleFilled className="text-red-500" />,
                  okButtonProps: { disabled: false },
                  cancelButtonProps: { disabled: false },
                });
                onFail?.(error);
                reject(error);
              });
            return promise;
          },
        });
      });
      return promise;
    },
    [modalPrompt],
  );

  const getLocalStorageData = useCallback(
    <T = unknown,>(key: string, defaultValue?: T): T | undefined => {
      const value = getValueByPath(localStorageValue, key);
      if (value == null) return defaultValue;
      return value as T;
    },
    [localStorageValue],
  );

  const setLocalStorageData = useCallback(
    (key: string, value: string | number | boolean | undefined | null) => {
      setValueByPath(localStorageValue, key, value);
      setLocalStorageValue({ ...localStorageValue });
      localStorage.setItem(
        BotAutoPortalLocalStorageKey,
        JSON.stringify(localStorageValue),
      );
    },
    [localStorageValue],
  );

  const contextValue = useMemo(
    () => ({
      hintPrompt,
      menuCollapse,
      changeMenuCollapse,
      copyText,
      modalPrompt,
      openConfirm,
      getLocalStorageData,
      setLocalStorageData,
    }),
    [
      hintPrompt,
      menuCollapse,
      changeMenuCollapse,
      copyText,
      modalPrompt,
      openConfirm,
      getLocalStorageData,
      setLocalStorageData,
    ],
  );

  useEffect(() => {
    const cb = (event: StorageEvent) =>
      handlerBotAutoLocalStorage(event, setLocalStorageValue);
    window.addEventListener("storage", cb);
    return () => window.removeEventListener("storage", cb);
  }, []);

  return (
    <GlobalContext.Provider value={contextValue}>
      {notificationContext}
      {contextHolder}
      <QueryProvider>
        <QueryClientProvider client={GlobalQueryClient}>
          {children}
        </QueryClientProvider>
      </QueryProvider>
    </GlobalContext.Provider>
  );
};

export { GlobalContext, GlobalContextProvider };
